import clsx from "clsx";
import { round } from "lodash-es";
import { ChangeEvent, type FC, useEffect, useState } from "react";

import {
  CHIP_BASE_CLASSNAME,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW,
  WHITE_CHIP_CLASSNAME,
} from "../interaction-mode/helpers";

import { CancelSaveMenu } from "components/menus/CancelSaveMenu";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button, ButtonProps } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { TextField } from "mds/components/TextField";
import { Tooltip } from "mds/components/Tooltip";
import { AssessPointsMarkerIcon, ChevronDownIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";

interface PointsConfigProps {
  page: PageType;
  studentPoints?: number;
  disabled?: boolean;
  readOnly?: boolean;
  size?: ButtonProps<"button">["size"];
  onSave: (points: number) => void;
  validatePoints?: (points: number) => boolean;
  saveTextKey?: "update" | "save";
  title?: string;
  decimal?: number;
  isPageOverviewHeader?: boolean;
}

const defaultValidationFunction = (points: number) => !isNaN(points) && points >= 0;

export const PointsConfig: FC<PointsConfigProps> = ({
  page,
  onSave,
  decimal = 0,
  disabled = false,
  readOnly = false,
  size = "s",
  title = "",
  validatePoints = defaultValidationFunction,
  studentPoints,
  saveTextKey,
  isPageOverviewHeader = false,
}) => {
  const { canAuthorSection } = useCapabilities();
  const [currentPoints, setCurrentPoints] = useState(
    studentPoints === undefined ? page.max_points : studentPoints,
  );
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const onPointsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    const newPoints = decimal ? round(parseFloat(input), decimal) : parseInt(input, 10);
    if (validatePoints(newPoints)) {
      setIsSaveDisabled(false);
      setCurrentPoints(newPoints);
    } else {
      setIsSaveDisabled(true);
    }
  };

  useEffect(() => {
    setCurrentPoints(studentPoints === undefined ? page.max_points : studentPoints);
  }, [studentPoints, page.max_points]);

  const text =
    studentPoints === undefined
      ? t("rubric_card.assessment_points", { count: page.max_points })
      : t("rubric_card.current_assessment_points", {
          max: page.max_points,
          current: studentPoints,
        });

  const shortText =
    studentPoints === undefined
      ? t("rubric_card.assessment_points_short", { count: page.max_points })
      : t("rubric_card.current_assessment_points", {
          max: page.max_points,
          current: studentPoints,
        });

  if (readOnly) {
    return (
      <div className={clsx(CHIP_BASE_CLASSNAME, WHITE_CHIP_CLASSNAME)}>
        <IconText
          className="gap-1.5 text-xs"
          containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
          containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
          iconStart={<AssessPointsMarkerIcon size="xs" />}
          shortText={shortText}
          text={text}
        />
      </div>
    );
  }

  return (
    <Tooltip text={disabled ? title : ""}>
      <CancelSaveMenu
        button={
          <Button
            className={`gap-1.5 ${size === "xs" ? "text-xs" : ""}`}
            disabled={disabled}
            kind="chipWhite"
            size={size}
            title={!disabled ? title : ""}
          >
            <IconText
              className="gap-1.5"
              containerQueryClassNameHide={
                isPageOverviewHeader ? PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE : undefined
              }
              containerQueryClassNameShow={
                isPageOverviewHeader ? PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW : undefined
              }
              iconStart={<AssessPointsMarkerIcon size={size} />}
              shortText={isPageOverviewHeader ? shortText : undefined}
              text={text}
            />
            {canAuthorSection && !disabled && <ChevronDownIcon size={size} />}
          </Button>
        }
        isSaveDisabled={isSaveDisabled}
        saveTextKey={saveTextKey}
        title={t("glossary.points")}
        onCancel={() =>
          setCurrentPoints(studentPoints === undefined ? page.max_points : studentPoints)
        }
        onSave={() => onSave(currentPoints || 0)}
      >
        <div className="flex w-full flex-col">
          <TextField
            className="w-full"
            eventName="Page - Update points"
            eventProperties={{ pageTitle: page.title, pageId: page.id }}
            min="0"
            placeholder={t("rubric_card.assessment_points", { count: 0 })}
            startIcon={<AssessPointsMarkerIcon />}
            title={t("tooltip.configure_points")}
            type="number"
            value={currentPoints || 0}
            isEditing
            onChange={(e) => onPointsChange(e)}
          />

          {isSaveDisabled && (
            <div className="body-xxs flex w-full justify-end text-red">
              {t("error.max_points_less_than_zero")}
            </div>
          )}
        </div>
      </CancelSaveMenu>
    </Tooltip>
  );
};
